import React from "react";

interface ChartTooltipProps {
  active?: boolean;
  payload?: any[];
  label?: string;
  showPercentage?: boolean;
  total?: number;
}

export const ChartTooltip = ({ active, payload, label, showPercentage = false, total }: ChartTooltipProps) => {
  if (!active || !payload || !payload.length) {
    return null;
  }

  // Calculate total if not provided and showPercentage is true
  const calculatedTotal = total || (showPercentage ? payload.reduce((sum, entry) => sum + (entry.value || 0), 0) : 0);

  return (
    <div className="bg-background border border-border rounded-lg shadow-lg p-3 min-w-[120px]">
      {label && (
        <p className="text-sm font-medium text-foreground mb-2">{label}</p>
      )}
      <div className="space-y-1">
        {payload.map((entry, index) => {
          const value = entry.value || 0;
          const percentage = showPercentage && calculatedTotal > 0 ? ((value / calculatedTotal) * 100).toFixed(1) : null;
          
          return (
            <div key={index} className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: entry.color || entry.fill || "hsl(var(--primary))" }}
                />
                <span className="text-sm text-muted-foreground">
                  {entry.name || entry.dataKey}
                </span>
              </div>
              <div className="text-right">
                <span className="text-sm font-medium text-foreground">
                  {typeof value === 'number' ? value.toLocaleString() : value}
                </span>
                {percentage && (
                  <div className="text-xs text-muted-foreground">
                    {percentage}%
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Custom tooltip for pie charts
export const PieChartTooltip = ({ active, payload }: ChartTooltipProps) => {
  if (!active || !payload || !payload.length) {
    return null;
  }

  const data = payload[0].payload;
  
  return (
    <div className="bg-background border border-border rounded-lg shadow-lg p-3 min-w-[120px]">
      <div className="flex items-center gap-2">
        <div 
          className="w-3 h-3 rounded-full" 
          style={{ backgroundColor: data.color || data.fill || "hsl(var(--primary))" }}
        />
        <span className="text-sm font-medium text-foreground">{data.name}</span>
      </div>
      <div className="mt-2 text-right">
        <div className="text-lg font-bold text-foreground">
          {data.value?.toLocaleString()}
        </div>
        {data.percentage && (
          <div className="text-sm text-muted-foreground">
            {data.percentage}%
          </div>
        )}
      </div>
    </div>
  );
};